/**
 * Zoekgebied: welke buurten de Funda-zoektocht afloopt, en de rekenkunde om van
 * een keuze buurten een handvol zoekopdrachten te maken.
 *
 * Je kiest buurten (CBS, op de kaart), maar Funda zoeken we op 4-cijferige
 * postcodes: die neemt Funda betrouwbaar aan als `selected_area`, buurtnamen
 * niet altijd (zie docs/zoekgebied-amsterdam.md). Elke buurt draagt daarom de
 * postcodes die hem raken; de actieve buurten samen geven een verzameling
 * postcodes, en die wordt in zo weinig mogelijk zoekopdrachten verdeeld die elk
 * ruim onder het paginaplafond blijven.
 *
 * Alles hier is pure rekenkunde zonder database, zodat de webapp bij elke klik
 * op de kaart meteen dezelfde teller en dezelfde opdeling laat zien die de API
 * bij het opslaan maakt.
 */
import { z } from 'zod';
import { formatAantal } from './formatteren';
import {
  aandeelPerPostcode,
  type BekendTelling,
  heeftZoekfilters,
  pasAandeelToe,
  type Zoekfilters,
  zelfdeZoekfilters,
  zoekfiltersQuerySchema,
  zoekfiltersSchema,
} from './zoekfilters';

/** Funda toont vijftien woningen per resultatenpagina. */
export const FUNDA_PER_PAGINA = 15;

/**
 * Hoeveel pagina's één zoekopdracht uit het zoekgebied hoogstens mag tellen.
 *
 * Het plafond van een ronde is sinds MA-28 twintig pagina's
 * (`RONDE_MAX_PAGINAS_STANDAARD`); een zoekopdracht die meer nodig heeft eindigt
 * als onvolledig en zet dan nooit iets op verdwenen. Zestien laat een vijfde
 * marge, zodat een groep die iets groeit niet meteen onvolledig wordt. Tot
 * MA-28 was dit zestig onder een plafond van tachtig.
 */
export const ZOEKGEBIED_PAGINAS_PER_OPDRACHT = 16;

/** Boven dit aantal pagina's per ronde waarschuwt het scherm; instelbaar. */
export const ZOEKGEBIED_GRENS_STANDAARD = 100;
export const ZOEKGEBIED_GRENS_MIN = 5;
export const ZOEKGEBIED_GRENS_MAX = 1000;

/** Zoekopdrachten uit het zoekgebied heten zo, gevolgd door hun postcodes. */
export const ZOEKGEBIED_NAAM_VOORVOEGSEL = 'Zoekgebied';

/** Een 4-cijferige postcode, zoals hij in `selected_area` staat. */
export const PC4_PATROON = /^\d{4}$/;

export function isPc4(waarde: string): boolean {
  return PC4_PATROON.test(waarde);
}

/* --- metingen -------------------------------------------------------------- */

/** Wat Funda voor een groep postcodes als totaal opgaf. */
export type Meting = {
  postcodes: readonly string[];
  gemeten: number;
  /** ISO-datum of -tijd; de nieuwste meting die een postcode bevat wint. */
  op: string;
  /**
   * De zoekfilters (MA-27) waarmee gemeten is; weggelaten of null is zonder
   * filters. Een gefilterd totaal zegt alleen iets over dezelfde filters.
   */
  filters?: Zoekfilters | null;
};

/**
 * De opdeling van Amsterdam zoals gemeten op 8 oktober 2026
 * (data/zoekopdrachten-amsterdam.json). Het startpunt van elke schatting zolang
 * er nog geen rondes met een `verwacht` zijn; daarna winnen die.
 */
export const GEMETEN_AMSTERDAM_2026_10_08: readonly Meting[] = [
  { postcodes: reeks(1011, 1015), gemeten: 548, op: '2026-10-08' },
  { postcodes: reeks(1016, 1019), gemeten: 718, op: '2026-10-08' },
  { postcodes: [...reeks(1021, 1028), ...reeks(1031, 1037)], gemeten: 701, op: '2026-10-08' },
  { postcodes: [...reeks(1041, 1047), ...reeks(1051, 1054)], gemeten: 448, op: '2026-10-08' },
  { postcodes: reeks(1055, 1059), gemeten: 573, op: '2026-10-08' },
  { postcodes: reeks(1060, 1064), gemeten: 407, op: '2026-10-08' },
  { postcodes: reeks(1065, 1069), gemeten: 694, op: '2026-10-08' },
  { postcodes: reeks(1071, 1079), gemeten: 875, op: '2026-10-08' },
  { postcodes: [...reeks(1081, 1083), ...reeks(1086, 1087)], gemeten: 518, op: '2026-10-08' },
  { postcodes: reeks(1091, 1098), gemeten: 824, op: '2026-10-08' },
  { postcodes: reeks(1101, 1109), gemeten: 634, op: '2026-10-08' },
];

function reeks(van: number, tot: number): string[] {
  const uit: string[] = [];
  for (let n = van; n <= tot; n++) uit.push(String(n));
  return uit;
}

/* --- schatten -------------------------------------------------------------- */

/**
 * Onder dit aantal bekende woningen in een gemeten groep verdelen we het
 * gemeten totaal naar de CBS-koopwoningen in plaats van naar wat wij kennen:
 * een handvol woningen zegt te weinig over hoe een groep is opgebouwd.
 */
export const MIN_BEKEND_VOOR_VERDELING = 30;

export type SchattingInvoer = {
  /** Alle postcodes waar een schatting voor nodig is. */
  postcodes: readonly string[];
  metingen: readonly Meting[];
  /** Woningen die wij nu actief kennen, per postcode. */
  bekend: ReadonlyMap<string, number>;
  /** Koopwoningen volgens het CBS (woningen × % koop), per postcode. */
  koopwoningen: ReadonlyMap<string, number>;
};

/**
 * Hoeveel woningen Funda per postcode ongeveer heeft.
 *
 * Funda geeft alleen een totaal per zoekopdracht. Per postcode verdelen we dat
 * totaal over de postcodes van de groep, naar rato van de woningen die we er al
 * kennen (of, als dat er te weinig zijn, de koopwoningen volgens het CBS). Een
 * postcode die in geen enkele meting voorkomt krijgt het landelijke gemiddelde
 * van die verhouding. De nieuwste meting die een postcode bevat wint, zodat
 * rondes de schatting vanzelf bijwerken.
 */
export function schatWoningenPerPostcode(invoer: SchattingInvoer): Map<string, number> {
  const { metingen, bekend, koopwoningen } = invoer;
  const uit = new Map<string, number>();
  const nieuwsteEerst = [...metingen]
    .filter((meting) => meting.postcodes.length > 0 && meting.gemeten >= 0)
    .sort((a, b) => (a.op < b.op ? 1 : a.op > b.op ? -1 : 0));

  const som = (postcodes: readonly string[], bron: ReadonlyMap<string, number>) =>
    postcodes.reduce((totaal, pc) => totaal + (bron.get(pc) ?? 0), 0);

  /* Voor postcodes zonder meting: hoeveel Funda-woningen er gemiddeld tegenover
     één bekende woning of één CBS-koopwoning staan, over alle metingen. */
  let gemetenTotaal = 0;
  let bekendTotaal = 0;
  let koopTotaal = 0;
  for (const meting of nieuwsteEerst) {
    gemetenTotaal += meting.gemeten;
    bekendTotaal += som(meting.postcodes, bekend);
    koopTotaal += som(meting.postcodes, koopwoningen);
  }

  for (const pc of invoer.postcodes) {
    const meting = nieuwsteEerst.find((een) => een.postcodes.includes(pc));
    if (meting) {
      const bekendInGroep = som(meting.postcodes, bekend);
      const koopInGroep = som(meting.postcodes, koopwoningen);
      let aandeel: number;
      if (bekendInGroep >= MIN_BEKEND_VOOR_VERDELING) {
        aandeel = (bekend.get(pc) ?? 0) / bekendInGroep;
      } else if (koopInGroep > 0) {
        aandeel = (koopwoningen.get(pc) ?? 0) / koopInGroep;
      } else {
        aandeel = 1 / meting.postcodes.length;
      }
      uit.set(pc, meting.gemeten * aandeel);
      continue;
    }

    const eigenBekend = bekend.get(pc) ?? 0;
    const eigenKoop = koopwoningen.get(pc) ?? 0;
    if (bekendTotaal > 0 && eigenBekend > 0) {
      uit.set(pc, (eigenBekend * gemetenTotaal) / bekendTotaal);
    } else if (koopTotaal > 0) {
      uit.set(pc, (eigenKoop * gemetenTotaal) / koopTotaal);
    } else {
      uit.set(pc, eigenBekend);
    }
  }
  return uit;
}

/* --- schatten met zoekfilters (MA-27) -------------------------------------- */

export type GefilterdeSchattingInvoer = SchattingInvoer & {
  /** Per postcode: hoeveel woningen we actief kennen en hoeveel daarvan voldoen. */
  bekendTelling: ReadonlyMap<string, BekendTelling>;
  filters: Zoekfilters;
};

export type GefilterdeSchatting = {
  zonderFilters: Map<string, number>;
  metFilters: Map<string, number>;
};

/**
 * De schatting per postcode, zonder en met de zoekfilters.
 *
 * Zonder filters: de metingen die zonder filters zijn gedaan (de elf van
 * 8 oktober, en elke ronde zonder filters in zijn URL), verdeeld zoals
 * `schatWoningenPerPostcode` dat doet. Met filters: datzelfde maal het aandeel
 * van de bekende woningen in die postcode dat door de filters komt. Heeft een
 * ronde met precies deze filters een groep al gemeten, dan wint dat totaal voor
 * die groep: het wordt over zijn postcodes verdeeld naar rato van de geschatte
 * gefilterde aantallen. Een gefilterde meting met andere filters telt nergens
 * mee - die zou dubbel filteren.
 */
export function schatMetZoekfilters(invoer: GefilterdeSchattingInvoer): GefilterdeSchatting {
  const zonderFilters = schatWoningenPerPostcode({
    ...invoer,
    metingen: invoer.metingen.filter((meting) => !heeftZoekfilters(meting.filters)),
  });
  if (!heeftZoekfilters(invoer.filters)) {
    return { zonderFilters, metFilters: new Map(zonderFilters) };
  }

  const aandeel = aandeelPerPostcode(invoer.postcodes, invoer.bekendTelling);
  const basis = pasAandeelToe(zonderFilters, aandeel);
  const metFilters = new Map(basis);

  const gelijk = invoer.metingen
    .filter(
      (meting) =>
        meting.postcodes.length > 0 &&
        meting.gemeten >= 0 &&
        meting.filters != null &&
        zelfdeZoekfilters(meting.filters, invoer.filters),
    )
    .sort((a, b) => (a.op < b.op ? 1 : a.op > b.op ? -1 : 0));

  for (const pc of invoer.postcodes) {
    const meting = gelijk.find((een) => een.postcodes.includes(pc));
    if (!meting) continue;
    const groepsom = meting.postcodes.reduce((som, een) => som + (basis.get(een) ?? 0), 0);
    const deel = groepsom > 0 ? (basis.get(pc) ?? 0) / groepsom : 1 / meting.postcodes.length;
    metFilters.set(pc, meting.gemeten * deel);
  }
  return { zonderFilters, metFilters };
}

/* --- groeperen ------------------------------------------------------------- */

export function paginasVoor(woningen: number): number {
  return Math.max(1, Math.ceil(Math.round(woningen) / FUNDA_PER_PAGINA));
}

export type Postcodegroep = {
  postcodes: string[];
  /** Geschat aantal woningen, afgerond. */
  woningen: number;
  paginas: number;
};

/**
 * De postcodes in zo weinig mogelijk groepen die elk onder `maxPaginas` blijven.
 *
 * Op volgorde van postcode, en dan vullen tot de volgende er niet meer bij
 * past. Voor een indeling die de volgorde aanhoudt is dat het kleinste aantal
 * groepen; en postcodes die naast elkaar liggen in de nummering liggen in
 * Amsterdam ook naast elkaar op de kaart, dus een groep is meestal een stuk
 * stad en de naam (`1071-1075`) zegt waar.
 *
 * Eén postcode die in zijn eentje al te groot is krijgt een eigen groep; die
 * kan niet kleiner.
 */
export function groepeerPostcodes(
  postcodes: readonly string[],
  schatting: ReadonlyMap<string, number>,
  maxPaginas = ZOEKGEBIED_PAGINAS_PER_OPDRACHT,
): Postcodegroep[] {
  const plafond = maxPaginas * FUNDA_PER_PAGINA;
  const gesorteerd = [...new Set(postcodes)].filter(isPc4).sort();
  const groepen: { postcodes: string[]; woningen: number }[] = [];

  let huidig: { postcodes: string[]; woningen: number } | null = null;
  for (const pc of gesorteerd) {
    const n = schatting.get(pc) ?? 0;
    if (huidig && huidig.woningen + n <= plafond) {
      huidig.postcodes.push(pc);
      huidig.woningen += n;
    } else {
      huidig = { postcodes: [pc], woningen: n };
      groepen.push(huidig);
    }
  }

  return groepen.map((groep) => ({
    postcodes: groep.postcodes,
    woningen: Math.round(groep.woningen),
    paginas: paginasVoor(groep.woningen),
  }));
}

export type Zoekplan = {
  postcodes: string[];
  groepen: Postcodegroep[];
  woningen: number;
  paginas: number;
};

/** Van actieve buurten naar postcodes, groepen en de teller. */
export function maakZoekplan(
  actieveBuurten: readonly string[],
  postcodesPerBuurt: ReadonlyMap<string, readonly string[]>,
  schatting: ReadonlyMap<string, number>,
  maxPaginas = ZOEKGEBIED_PAGINAS_PER_OPDRACHT,
): Zoekplan {
  const postcodes = new Set<string>();
  for (const code of actieveBuurten) {
    for (const pc of postcodesPerBuurt.get(code) ?? []) postcodes.add(pc);
  }
  const groepen = groepeerPostcodes([...postcodes], schatting, maxPaginas);
  return {
    postcodes: [...postcodes].sort(),
    groepen,
    woningen: groepen.reduce((totaal, groep) => totaal + groep.woningen, 0),
    paginas: groepen.reduce((totaal, groep) => totaal + groep.paginas, 0),
  };
}

/* --- doorlooptijd (MA-32) -------------------------------------------------- */

/**
 * Hoe vaak de extensie standaard kijkt of er iets aan de beurt is, in minuten.
 *
 * Het ritme is een instelling van de extensie (`wekkerMinuten` in de opties,
 * 10 tot 120) en de server kent hem niet; de teller rekent daarom met de
 * standaard en zegt dat erbij. De extensie heeft geen build en kan dit bestand
 * niet importeren: `apps/extensie/bron/achtergrond.js` draagt dezelfde waarde.
 */
export const WEKKER_MINUTEN_STANDAARD = 30;

export type DoorlooptijdInvoer = {
  /** Pagina's van een volledige ronde over het zoekgebied. */
  paginas: number;
  /** Zoekopdrachten (groepen) waarin die ronde is opgedeeld. */
  groepen: number;
  dagplafond: number;
  wekkerMinuten?: number;
};

export type Doorlooptijd = {
  /** Minuten bladeren: één groep per wekker-tik, dus groepen × ritme. */
  minuten: number;
  /** Kalenderdagen die een volledige ronde minstens vraagt; 0 zonder plan. */
  dagen: number;
};

/**
 * Hoe lang een volledige ronde over het zoekgebied duurt.
 *
 * De extensie doet één zoekopdracht per wekker-tik, dus negen groepen bij een
 * ritme van dertig minuten zijn in viereneenhalf uur rond - mits het dagplafond
 * het toelaat. Honderdtwintig pagina's onder een plafond van vijftig zijn drie
 * dagen, wat het ritme ook is; en zestig groepen van een half uur passen ook
 * met een ruim plafond niet in één dag. De dagen zijn daarom het grootste van
 * de twee: wat het plafond vraagt en wat het ritme vraagt.
 */
export function rondeDoorlooptijd(invoer: DoorlooptijdInvoer): Doorlooptijd {
  const ritme = invoer.wekkerMinuten ?? WEKKER_MINUTEN_STANDAARD;
  if (invoer.paginas <= 0 || invoer.groepen <= 0) return { minuten: 0, dagen: 0 };
  const minuten = invoer.groepen * ritme;
  const doorPlafond = Math.ceil(invoer.paginas / Math.max(1, invoer.dagplafond));
  const doorRitme = Math.ceil(minuten / 1440);
  return { minuten, dagen: Math.max(1, doorPlafond, doorRitme) };
}

/** `270` -> `4,5 uur`; `45` -> `45 min`; `60` -> `1 uur`. */
export function formatDuur(minuten: number): string {
  if (minuten < 60) return `${Math.round(minuten)} min`;
  const uren = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 1 }).format(minuten / 60);
  return `${uren} uur`;
}

/**
 * Eén stuk tekst voor achter de teller:
 * `≈ 4,5 uur bij 30 min per groep · met plafond 50: ≈ 3 dagen`.
 * Leeg zonder plan, zodat de regel niets zegt over een leeg zoekgebied.
 */
export function doorlooptijdLabel(invoer: DoorlooptijdInvoer): string {
  const ritme = invoer.wekkerMinuten ?? WEKKER_MINUTEN_STANDAARD;
  const tijd = rondeDoorlooptijd(invoer);
  if (tijd.dagen === 0) return '';
  const dagen = `${formatAantal(tijd.dagen)} ${tijd.dagen === 1 ? 'dag' : 'dagen'}`;
  return `≈ ${formatDuur(tijd.minuten)} bij ${ritme} min per groep · met plafond ${formatAantal(invoer.dagplafond)}: ≈ ${dagen}`;
}

/**
 * Hoe lang een volledige ronde duurt met vaste tijden (MA-46): per dag gaan er
 * hoogstens `tijden × paginas` pagina's door, en niet meer dan het dagplafond.
 *
 * `≈ 70 pagina's · 2× per dag 35: elke dag rond`
 * `≈ 120 pagina's · 2× per dag 35: ≈ 2 dagen`
 */
export function vensterDoorlooptijdLabel(invoer: {
  paginas: number;
  tijden: number;
  perKeer: number;
  dagplafond: number;
}): string {
  if (invoer.paginas <= 0 || invoer.tijden <= 0) return '';
  const perDag = Math.max(1, Math.min(invoer.tijden * invoer.perKeer, invoer.dagplafond));
  const dagen = Math.ceil(invoer.paginas / perDag);
  const duur = dagen <= 1 ? 'elke dag rond' : `≈ ${formatAantal(dagen)} dagen`;
  return `≈ ${formatAantal(invoer.paginas)} pagina's · ${invoer.tijden}× per dag ${formatAantal(invoer.perKeer)}: ${duur}`;
}

/* --- namen ----------------------------------------------------------------- */

/** `["1071","1072","1073","1075"]` -> `1071-1073, 1075`. */
export function compactePostcodes(postcodes: readonly string[]): string {
  const getallen = [...new Set(postcodes)]
    .filter(isPc4)
    .map(Number)
    .sort((a, b) => a - b);
  const delen: string[] = [];
  let i = 0;
  while (i < getallen.length) {
    let j = i;
    while (j + 1 < getallen.length && getallen[j + 1] === (getallen[j] ?? 0) + 1) j++;
    delen.push(i === j ? String(getallen[i]) : `${getallen[i]}-${getallen[j]}`);
    i = j + 1;
  }
  return delen.join(', ');
}

/**
 * De naam van een zoekopdracht uit het zoekgebied. De naam is ook de feed
 * (zie de `zoekopdracht`-tabel); hij volgt de postcodes, dus twee groepen met
 * verschillende postcodes heten nooit hetzelfde.
 */
export function zoekgebiedNaam(postcodes: readonly string[]): string {
  const kort = compactePostcodes(postcodes);
  const naam = `${ZOEKGEBIED_NAAM_VOORVOEGSEL} ${kort}`;
  if (naam.length <= 110) return naam;
  const gesorteerd = [...new Set(postcodes)].filter(isPc4).sort();
  return `${ZOEKGEBIED_NAAM_VOORVOEGSEL} ${gesorteerd[0]}-${gesorteerd.at(-1)} (${gesorteerd.length} postcodes)`;
}

/* --- contracten ------------------------------------------------------------ */

export const buurtcodeSchema = z
  .string()
  .trim()
  .regex(/^BU\d{4}[0-9A-Z]{4}$/, 'Een buurtcode ziet eruit als BU0363KE04.');

/** Eén buurt op de kaart van het zoekgebied. */
export const zoekgebiedBuurtSchema = z.object({
  buurtcode: z.string(),
  buurtnaam: z.string(),
  wijkcode: z.string(),
  wijknaam: z.string(),
  postcodes: z.array(z.string()),
});
export type ZoekgebiedBuurt = z.infer<typeof zoekgebiedBuurtSchema>;

/**
 * GET /beheer/buurten: de buurten als GeoJSON. De geometrie wordt niet tot op
 * de coördinaat gecontroleerd - dat is MapLibre's werk - alleen de vorm. Welke
 * buurten aan staan zit niet hierin maar in de stand, zodat dit antwoord een
 * jaar hetzelfde blijft en mag blijven hangen.
 */
export const buurtenKaartSchema = z.object({
  type: z.literal('FeatureCollection'),
  peiljaar: z.number().int().nullable(),
  features: z.array(
    z.object({
      type: z.literal('Feature'),
      id: z.number().int(),
      properties: zoekgebiedBuurtSchema,
      geometry: z.object({
        type: z.enum(['Polygon', 'MultiPolygon']),
        coordinates: z.array(z.unknown()),
      }),
    }),
  ),
});
export type BuurtenKaart = z.infer<typeof buurtenKaartSchema>;

export const postcodegroepSchema = z.object({
  postcodes: z.array(z.string()),
  woningen: z.number().int().nonnegative(),
  paginas: z.number().int().nonnegative(),
});

export const bekendTellingSchema = z.object({
  totaal: z.number().int().nonnegative(),
  voldoet: z.number().int().nonnegative(),
});

/**
 * GET /beheer/zoekgebied/schatting: de schatting per postcode voor een set
 * zoekfilters, zodat de teller meebeweegt terwijl je de filters bijstelt.
 */
export const zoekgebiedSchattingSchema = z.object({
  filters: zoekfiltersSchema,
  /** Geschat aantal Funda-woningen per postcode, met de filters. */
  schatting: z.record(z.string(), z.number().nonnegative()),
  /** Dezelfde schatting zonder filters, voor de vergelijking op het scherm. */
  schattingZonderFilters: z.record(z.string(), z.number().nonnegative()),
  /** Per postcode: bekende actieve woningen, en hoeveel daarvan voldoen. */
  bekend: z.record(z.string(), bekendTellingSchema),
});
export type ZoekgebiedSchatting = z.infer<typeof zoekgebiedSchattingSchema>;

/** GET /beheer/zoekgebied: de keuze, de filters, de schatting en wat er nu zoekt. */
export const zoekgebiedStandSchema = zoekgebiedSchattingSchema.extend({
  /** Buurtcodes die aan staan. */
  aan: z.array(z.string()),
  plan: z.object({
    postcodes: z.array(z.string()),
    groepen: z.array(postcodegroepSchema),
    woningen: z.number().int().nonnegative(),
    paginas: z.number().int().nonnegative(),
  }),
  grensPaginas: z.number().int().positive(),
  paginasPerOpdracht: z.number().int().positive(),
  /** Of er al buurten in de database staan (`pnpm db:haal-buurten`). */
  buurtenAanwezig: z.boolean(),
  /**
   * Of de zoekopdrachten van het zoekgebied precies deze filters in hun URL
   * hebben. Niet na een migratie die de standaard zette, of na een handmatige
   * ingreep in de URL: dan zet opslaan ze weer gelijk.
   */
  urlsActueel: z.boolean(),
});
export type ZoekgebiedStand = z.infer<typeof zoekgebiedStandSchema>;

export const zoekgebiedOpslaanSchema = z.object({
  buurtcodes: z.array(buurtcodeSchema).max(1000),
  /** Weggelaten laat de opgeslagen filters staan. */
  filters: zoekfiltersSchema.optional(),
});
export type ZoekgebiedOpslaan = z.infer<typeof zoekgebiedOpslaanSchema>;

/**
 * GET /beheer/zoekgebied/budget-gevolg: hoeveel bekende actieve woningen in
 * het zoekgebied uit de zoekopdrachten vallen als deze filters worden
 * opgeslagen, door het budget alleen: wat nu door de opgeslagen filters komt,
 * ook door de nieuwe oppervlakte en labels zou komen, maar boven het nieuwe
 * budget ligt. Die staan na de volgende volledige ronde op *verdwenen* (uit
 * het aanbod). Dezelfde query als /schatting, met het budget verplicht.
 */
export const budgetGevolgQuerySchema = zoekfiltersQuerySchema.extend({
  maxVraagprijs: z.coerce.number().int().positive().max(100_000_000),
});
export const budgetGevolgSchema = z.object({
  maxVraagprijs: z.number().int().positive(),
  vallenUit: z.number().int().nonnegative(),
});
export type BudgetGevolg = z.infer<typeof budgetGevolgSchema>;

export const zoekgebiedGrensSchema = z.object({
  grensPaginas: z.number().int().min(ZOEKGEBIED_GRENS_MIN).max(ZOEKGEBIED_GRENS_MAX),
});
export type ZoekgebiedGrens = z.infer<typeof zoekgebiedGrensSchema>;

/** Wat opslaan met de zoekopdrachten deed, voor een regel op het scherm. */
export const zoekgebiedUitslagSchema = z.object({
  stand: zoekgebiedStandSchema,
  nieuw: z.array(z.string()),
  bijgewerkt: z.array(z.string()),
  aangezet: z.array(z.string()),
  uitgezet: z.array(z.string()),
  ongewijzigd: z.array(z.string()),
});
export type ZoekgebiedUitslag = z.infer<typeof zoekgebiedUitslagSchema>;
